import styled from 'styled-components';
import { ReactComponent as InspectIcon } from '@elementary/common-ui/assets/24x24/inspect.svg';
import { ReactComponent as AddTestIcon } from '@elementary/common-ui/assets/add-test.svg';
import { ReactComponent as ColumnsIcon } from '@elementary/common-ui/assets/columns.svg';
import { ReactComponent as TablesIcon } from '@elementary/common-ui/assets/tables.svg';
import {
  Button,
  InternalLink,
  Menu,
  SplitButton,
} from '@elementary/common-ui/components';
import { Props as ButtonProps } from '@elementary/common-ui/components/Button/Button';
import type { Option } from '@elementary/common-ui/components/Menu/Menu';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import useDataTestsEnabled from '@elementary/common-ui/hooks/useDataTestsEnabled';
import useAiChatPanel from '@elementary/common-ui/hooks/useAiChatPanel';
import useHasDbtProjects from '@elementary/common-ui/hooks/useHasDbtProjects';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { Typography } from '@mui/material';
import { useCodeRepositoryIntegration } from 'shared/hooks/integrations';
import { WizardResourceType } from 'testManagement/types';
import { fontSize, fontWeight } from '@elementary/common-ui/style/utils';

const TooltipText = styled(Typography)`
  font-size: ${fontSize('sm')};
  font-weight: ${fontWeight('regular')};
  color: ${({ theme }) => theme.palette.content.inverse};
`;

const DisabledTooltip = () => (
  <TooltipText>
    <InternalLink to="/settings/environments">Connect</InternalLink> your code
    repository to add tests directly from the UI.
  </TooltipText>
);

const ColumnsErrorAlert = () => (
  <TooltipText sx={{ padding: 0.5 }}>
    Column information could not be retrieved.
  </TooltipText>
);

type AddTestMenuOption = Omit<Option, 'value'> & {
  value: WizardResourceType;
};

type Props = {
  onChange: (item: WizardResourceType) => void;
  size?: 'medium' | 'large';
  buttonProps?: ButtonProps;
  disableTableTests?: boolean;
  disableColumnTests?: boolean;
  disableCustomQueryTests?: boolean;
  useAiAsPrimary?: boolean;
};

const AddTestMenuButton = ({
  onChange,
  size = 'medium',
  buttonProps = {},
  disableTableTests,
  disableColumnTests,
  disableCustomQueryTests,
  useAiAsPrimary = false,
  ...menuProps
}: Props) => {
  const selectedEnvId = useSelectedEnv();
  const { data: account } = useAccount();
  const { openPanel } = useAiChatPanel();
  const hasDbtProjects = useHasDbtProjects();

  const { shouldConnectCodeRepo } = useCodeRepositoryIntegration({
    envId: selectedEnvId,
  });
  const isDataTestsEnabled = useDataTestsEnabled();
  const canAddCustomQuery = hasDbtProjects || isDataTestsEnabled;
  const isCustomQueryDisabledByRepo =
    shouldConnectCodeRepo && !isDataTestsEnabled;

  const addTestMenuOptions: AddTestMenuOption[] = [
    {
      value: 'tables',
      label: 'Table test',
      icon: <TablesIcon width={20} />,
      disabled: disableTableTests,
      tooltip: shouldConnectCodeRepo ? 'Only cloud tests can be added.' : '',
    },
    ...(hasDbtProjects
      ? [
          {
            value: 'columns' as const,
            label: 'Column test',
            icon: <ColumnsIcon width={20} />,
            disabled: shouldConnectCodeRepo || disableColumnTests,
            tooltip: shouldConnectCodeRepo ? (
              <DisabledTooltip />
            ) : disableColumnTests ? (
              <ColumnsErrorAlert />
            ) : null,
          },
        ]
      : []),
    ...(canAddCustomQuery
      ? [
          {
            value: 'customQuery' as const,
            label: 'Custom query test',
            icon: <InspectIcon width={20} />,
            disabled: isCustomQueryDisabledByRepo || disableCustomQueryTests,
            tooltip: isCustomQueryDisabledByRepo && <DisabledTooltip />,
          },
        ]
      : []),
  ];

  if (useAiAsPrimary && account?.ai_features_enabled) {
    const variant =
      buttonProps.variant === 'outlined' || buttonProps.variant === 'contained'
        ? buttonProps.variant
        : undefined;

    return (
      <SplitButton
        size={size}
        startIcon={<AddTestIcon />}
        menuTitle="Add tests manually"
        variant={variant}
        color={buttonProps.color}
        disabled={buttonProps.disabled}
        className={buttonProps.className}
        actions={addTestMenuOptions.map((option) => ({
          label: option.label,
          icon: option.icon,
          disabled: option.disabled,
          tooltip: option.tooltip,
          onClick: () => onChange(option.value),
        }))}
        onClick={() =>
          openPanel({
            starterKey: 'addTests',
            origin: 'ai_cta',
          })
        }
      >
        Add test
      </SplitButton>
    );
  }

  return (
    <Menu
      menuButton={
        <Button size={size} startIcon={<AddTestIcon />} {...buttonProps}>
          Add test
        </Button>
      }
      options={addTestMenuOptions}
      closeOnChange
      onChange={(item) => onChange(item as WizardResourceType)}
      {...menuProps}
    />
  );
};

export default AddTestMenuButton;
